import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';

// Searchable Select Component (Select2-like) - Fixed version
const SearchableSelect = ({ options, value, onChange, placeholder, labelKey, valueKey = 'id', loading, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on search
  const filteredOptions = options.filter(option =>
    option[labelKey]?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Show only 5 items when no search term, all matching when searching
  const displayOptions = searchTerm.length > 0 
    ? filteredOptions.slice(0, 50) // Show up to 50 when searching
    : filteredOptions.slice(0, 5);  // Show only 5 by default

  const selectedOption = options.find(opt => opt[valueKey] === value);

  // Clean display text (remove quotes)
  const cleanText = (text) => {
    if (!text) return text;
    return text.replace(/^["']|["']$/g, '').trim();
  };

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative" ref={wrapperRef} style={{ zIndex: isOpen ? 100 : 1 }}>
      <div
        className={`form-input cursor-pointer flex items-center justify-between ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={`truncate ${selectedOption ? 'text-white' : 'text-text-muted'}`}>
          {selectedOption ? cleanText(selectedOption[labelKey]) : placeholder}
        </span>
        <span className={`ml-2 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </div>

      {isOpen && !disabled && (
        <div 
          ref={dropdownRef}
          className="absolute left-0 w-full mt-2 bg-[#1a1a2e] border border-glass-border rounded-xl shadow-2xl overflow-hidden"
          style={{ 
            zIndex: 9999,
            maxHeight: '280px',
            position: 'absolute',
            top: '100%'
          }}
        >
          {/* Search Input */}
          <div className="p-3 border-b border-glass-border sticky top-0 bg-[#1a1a2e]">
            <input
              type="text"
              className="w-full px-4 py-2 bg-glass-bg border border-glass-border rounded-lg text-white text-sm focus:outline-none focus:border-accent-gold"
              placeholder="Type to search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
            {searchTerm.length === 0 && options.length > 5 && (
              <p className="text-xs text-text-muted mt-1">Showing 5 of {options.length}. Type to search more...</p>
            )}
          </div>

          {/* Options List */}
          <div className="overflow-y-auto" style={{ maxHeight: '200px' }}>
            {loading ? (
              <div className="p-4 text-center text-text-muted">Loading...</div>
            ) : displayOptions.length === 0 ? (
              <div className="p-4 text-center text-text-muted">
                {searchTerm ? 'No results found' : 'No options available'}
              </div>
            ) : (
              displayOptions.map((option) => (
                <div
                  key={option[valueKey]}
                  className={`px-4 py-3 cursor-pointer transition-colors hover:bg-primary-purple/30 ${
                    value === option[valueKey] ? 'bg-primary-purple/50 text-accent-gold' : 'text-white'
                  }`}
                  onClick={() => handleSelect(option[valueKey])}
                >
                  {cleanText(option[labelKey])}
                </div>
              ))
            )}
            {searchTerm.length > 0 && filteredOptions.length > 50 && (
              <div className="p-3 text-center text-text-muted text-sm border-t border-glass-border">
                Showing 50 of {filteredOptions.length} results. Refine your search.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Result Card Component - Compact version
const ResultCard = ({ result }) => {
  // Clean display text
  const cleanText = (text) => {
    if (!text) return text;
    return text.replace(/^["']|["']$/g, '').trim();
  };

  return (
    <div className="glass-card p-5 hover:border-accent-gold/50 transition-all h-full flex flex-col">
      {/* University Name */}
      <h3 className="text-base font-bold text-accent-gold mb-1 uppercase tracking-wide line-clamp-2">
        {result.university_name}
      </h3>
      
      {/* Program/Course Name */}
      <h4 className="text-sm font-semibold text-white mb-3 line-clamp-2">
        {cleanText(result.course_name) || cleanText(result.program_name)}
      </h4>

      {/* Mandatory Subjects */}
      {result.eligibility_subjects && (
        <div className="mb-3 p-3 bg-white/5 rounded-lg flex-grow">
          <p className="text-xs text-white leading-relaxed">
            <span className="font-semibold text-light-purple block mb-1">Mandatory subjects CUET(UG):</span>
            <span className="text-text-light line-clamp-4">{result.eligibility_subjects}</span>
          </p>
        </div>
      )}

      {/* Eligibility */}
      {result.eligibility_criteria && (
        <div className="p-3 bg-white/5 rounded-lg">
          <p className="text-xs text-white leading-relaxed">
            <span className="font-semibold text-light-purple block mb-1">Eligibility:</span>
            <span className="text-text-light line-clamp-4">{result.eligibility_criteria}</span>
          </p>
        </div>
      )}

      {/* No eligibility info */}
      {!result.eligibility_subjects && !result.eligibility_criteria && (
        <div className="p-3 bg-white/5 rounded-lg flex-grow">
          <p className="text-xs text-text-muted italic">
            Eligibility details not available. Please check university website.
          </p>
        </div>
      )}
    </div>
  );
};

const SearchSection = () => {
  // Dropdown data
  const [degrees, setDegrees] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [programs, setPrograms] = useState([]);
  
  // Selected values
  const [selectedDegree, setSelectedDegree] = useState('');
  const [selectedUniversity, setSelectedUniversity] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  
  // Results
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  
  // Loading states
  const [loadingDegrees, setLoadingDegrees] = useState(true);
  const [loadingUniversities, setLoadingUniversities] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [searching, setSearching] = useState(false);

  // Fetch degrees on mount
  useEffect(() => {
    const fetchDegrees = async () => {
      try {
        const response = await api.get('/search/degrees');
        if (response.data.success) {
          setDegrees(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching degrees:', error);
      } finally {
        setLoadingDegrees(false);
      }
    };
    fetchDegrees();
  }, []);

  // Fetch universities (filtered by degree when selected)
  const fetchUniversities = useCallback(async (degreeId) => {
    setLoadingUniversities(true);
    try {
      const params = degreeId ? `?degreeId=${degreeId}` : '';
      const response = await api.get(`/search/universities${params}`);
      if (response.data.success) {
        setUniversities(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching universities:', error);
    } finally {
      setLoadingUniversities(false);
    }
  }, []);

  // Fetch programs (filtered by degree and/or university)
  const fetchPrograms = useCallback(async (degreeId, universityId) => {
    setLoadingPrograms(true);
    try {
      const params = new URLSearchParams();
      if (degreeId) params.append('degreeId', degreeId);
      if (universityId) params.append('universityId', universityId);
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await api.get(`/search/programs${queryString}`);
      if (response.data.success) {
        setPrograms(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoadingPrograms(false);
    }
  }, []);

  // Initial load - fetch all universities
  useEffect(() => {
    fetchUniversities(null);
  }, [fetchUniversities]);

  // When degree changes - update universities and programs
  useEffect(() => {
    // Reset dependent selections
    setSelectedUniversity('');
    setSelectedProgram('');
    
    // Fetch filtered universities and programs
    fetchUniversities(selectedDegree || null);
    fetchPrograms(selectedDegree || null, null);
  }, [selectedDegree, fetchUniversities, fetchPrograms]);

  // When university changes - update programs
  useEffect(() => {
    // Reset program selection
    setSelectedProgram('');
    
    // Fetch programs filtered by both degree and university
    fetchPrograms(selectedDegree || null, selectedUniversity || null);
  }, [selectedUniversity, selectedDegree, fetchPrograms]);

  // Handle Search
  const handleSearch = async () => {
    if (!selectedDegree && !selectedUniversity && !selectedProgram) {
      alert('Please select at least one filter');
      return;
    }

    setSearching(true);
    setSearched(true);
    
    try {
      const response = await api.post('/search/eligibility', {
        degreeId: selectedDegree || null,
        universityId: selectedUniversity || null,
        programId: selectedProgram || null
      });
      
      if (response.data.success) {
        setResults(response.data.data);
      }
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Handle Reset
  const handleReset = () => {
    setSelectedDegree('');
    setSelectedUniversity('');
    setSelectedProgram('');
    setResults([]);
    setSearched(false);
    
    // Reload all universities
    fetchUniversities(null);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-dark-purple/20 to-secondary-purple/20 relative" id="search">
      <div className="max-w-[1400px] mx-auto px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="section-title">Eligibility - Course requirements - Universities</h2>
          <span className="section-subtitle">
            <div className="hero-badge">University of Delhi</div>  <div className="hero-badge">Assam University</div> <div className="hero-badge">Tezpur University</div> <div className="hero-badge">Gauhati University</div>
          </span>
        </div>

        {/* Search Form */}
        <div className="glass-card-overflow max-w-4xl mx-auto mb-8 relative" style={{ zIndex: 50 }}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* Degree Dropdown */}
            <div style={{ position: 'relative', zIndex: 30 }}>
              <label className="block text-text-muted text-sm mb-2 font-medium">Degree</label>
              <SearchableSelect
                options={degrees}
                value={selectedDegree}
                onChange={setSelectedDegree}
                placeholder="Select Degree"
                labelKey="degree"
                loading={loadingDegrees}
              />
            </div>

            {/* University Dropdown */}
            <div style={{ position: 'relative', zIndex: 20 }}>
              <label className="block text-text-muted text-sm mb-2 font-medium">University</label>
              <SearchableSelect
                options={universities}
                value={selectedUniversity}
                onChange={setSelectedUniversity}
                placeholder="Select University"
                labelKey="university_name"
                loading={loadingUniversities}
              />
            </div>

            {/* Program Dropdown */}
            <div style={{ position: 'relative', zIndex: 10 }}>
              <label className="block text-text-muted text-sm mb-2 font-medium">Program</label>
              <SearchableSelect
                options={programs}
                value={selectedProgram}
                onChange={setSelectedProgram}
                placeholder="Select Program"
                labelKey="program_name"
                loading={loadingPrograms}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-center gap-4 pt-4">
            <button
              onClick={handleSearch}
              disabled={searching}
              className="btn-primary px-8 py-3 flex items-center gap-2"
            >
              {searching ? (
                <>
                  <span className="animate-spin">⏳</span> Searching...
                </>
              ) : (
                <>
                  🔍 Search
                </>
              )}
            </button>
            <button
              onClick={handleReset}
              className="btn-secondary px-8 py-3"
            >
              ↺ Reset
            </button>
          </div>
        </div>

        {/* Results Section */}
        {searched && (
          <div className="max-w-6x4 mx-auto">
            {searching ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-4 animate-bounce">🔍</div>
                <p className="text-text-muted">Searching...</p>
              </div>
            ) : results.length === 0 ? (
              <div className="glass-card text-center py-12">
                <div className="text-4xl mb-4">📭</div>
                <h3 className="text-xl text-white mb-2">No Results Found</h3>
                <p className="text-text-muted">Try different filter combinations.</p>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-white">
                    📋 Search Results 
                    <span className="ml-2 text-accent-gold">({results.length} found)</span>
                  </h3>
                </div>
                
                {/* Grid Layout - Max 4 columns */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {results.map((result, index) => (
                    <ResultCard key={result.id || index} result={result} />
                  ))}
                </div>

                {results.length >= 100 && (
                  <div className="text-center mt-6 text-text-muted">
                    <p>Showing first 100 results. Refine your search for more specific results.</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default SearchSection;